import { Box, Modal, Typography } from "@mui/material";
import { useCallback } from "react";
import ButtonComponent from "../../../components/button/ButtonComponent";

interface MessageModalProps {
  message: string;
  visibleMessage: boolean;
  setVisibleMessage: (visibleMessage: boolean) => void;
}

const styles = {
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  container: {
    p: 4,
    backgroundColor: "var(--white)",
    borderRadius: 2,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minWidth: "400px",
    boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1)",
    transition: "transform 0.2s ease",
    "&:focus": {
      outline: "none",
    },
  },
  message: {
    fontSize: 20,
    fontWeight: "bold",
    color: "var(--gray-700)",
    textAlign: "center",
  },
  button: {
    mt: 4,
    width: "auto",
    px: 3,
    mx: 0,
    borderRadius: 1.5,
  },
};

/**
 * MessageModal Component
 * This component renders a modal dialog with a message and a close button.
 * It uses Material-UI's Modal and Box components for styling and layout.
 * @param {object} props - The props for the MessageModal component.
 * @param {string} props.message - The message to display inside the modal.
 * @param {boolean} props.visibleMessage - A boolean indicating whether the modal is visible.
 * @param {Function} props.setVisibleMessage - A function to update the visibility state of the modal.
 */
export default function MessageModal({
  message,
  visibleMessage,
  setVisibleMessage,
}: MessageModalProps) {
  /**
   * Handles the closing of the modal by setting the visibility to false.
   * This function is memoized using useCallback to prevent unnecessary re-renders.
   */
  const handleClose = useCallback(() => {
    setVisibleMessage(false);
  }, [setVisibleMessage]);

  return (
    <Modal
      open={visibleMessage}
      onClose={handleClose}
      sx={styles.modal}
      aria-labelledby="message-modal"
      closeAfterTransition
    >
      <Box sx={styles.container} tabIndex={-1} role="dialog" aria-modal="true">
        <Typography id="message-modal" sx={styles.message}>
          {message}
        </Typography>
        <ButtonComponent label="閉じる" onClick={handleClose} sx={styles.button} theme="default" />
      </Box>
    </Modal>
  );
}
